/**
 * PAYMENT METHOD MANAGER
 * 
 * Admin component for managing payment methods.
 * Payment methods define how donors can pay.
 * 
 * @module Donations/Components/Settings
 */

import React, { useState, useEffect } from 'react';
import {
    Plus,
    Edit,
    Trash2,
    Save,
    X,
    Eye,
    EyeOff,
    AlertCircle,
    CheckCircle,
    Loader2,
    Smartphone,
    Building2,
    CreditCard,
    DollarSign,
    Wallet,
    Banknote,
    Landmark,
    Globe,
    Lock,
    Shield,
    Zap,
    Phone,
    Radio,
    Wifi,
    Signal,
    Network,
    Cpu,
    Database,
    Server,
    Cloud,
    Sun,
    Moon,
    Star,
    Heart,
    Users,
    HandCoins,
    PiggyBank,
    Receipt,
    QrCode,
    ScanLine,
    Send,
    ArrowUpRight,
    ArrowDownLeft,
    Repeat,
    Clock,
    Calendar,
    Check,
    X as XIcon,
    AlertTriangle,
    Info,
    HelpCircle
} from 'lucide-react';

// ============================================
// TYPES
// ============================================

interface PaymentMethod {
    _id?: string;
    name: string;
    slug: string;
    type: 'mobile_money' | 'bank_transfer' | 'card' | 'other';
    provider?: string;
    icon: string;
    isActive: boolean;
    sortOrder: number;
    instructions?: string;
    isInternational?: boolean;
    createdAt?: Date;
    updatedAt?: Date;
}

interface PaymentMethodManagerProps {
    onSave?: (methods: PaymentMethod[]) => void;
}

// ============================================
// ICON CONFIGURATION
// ============================================

const ICON_MAP: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
    Smartphone,
    Building2,
    CreditCard,
    DollarSign,
    Wallet,
    Banknote,
    Landmark,
    Globe,
    Lock,
    Shield,
    Zap,
    Phone,
    Radio,
    Wifi,
    Signal,
    Network,
    Cpu,
    Database,
    Server,
    Cloud,
    Sun,
    Moon,
    Star,
    Heart,
    Users,
    HandCoins,
    PiggyBank,
    Receipt,
    QrCode,
    ScanLine,
    Send,
    ArrowUpRight,
    ArrowDownLeft,
    Repeat,
    Clock,
    Calendar,
    Check,
    XIcon,
    AlertTriangle,
    Info,
    HelpCircle
};

const AVAILABLE_ICONS = [
    { name: 'Smartphone', icon: Smartphone, category: 'Mobile' },
    { name: 'Building2', icon: Building2, category: 'Banking' },
    { name: 'CreditCard', icon: CreditCard, category: 'Card' },
    { name: 'DollarSign', icon: DollarSign, category: 'Currency' },
    { name: 'Wallet', icon: Wallet, category: 'Payment' },
    { name: 'Banknote', icon: Banknote, category: 'Currency' },
    { name: 'Landmark', icon: Landmark, category: 'Banking' },
    { name: 'Globe', icon: Globe, category: 'International' },
    { name: 'Lock', icon: Lock, category: 'Security' },
    { name: 'Shield', icon: Shield, category: 'Security' },
    { name: 'Zap', icon: Zap, category: 'Fast' },
    { name: 'Phone', icon: Phone, category: 'Mobile' },
    { name: 'Radio', icon: Radio, category: 'Mobile' },
    { name: 'Wifi', icon: Wifi, category: 'Mobile' },
    { name: 'Signal', icon: Signal, category: 'Mobile' },
    { name: 'Network', icon: Network, category: 'Mobile' },
    { name: 'HandCoins', icon: HandCoins, category: 'Payment' },
    { name: 'PiggyBank', icon: PiggyBank, category: 'Payment' },
    { name: 'Receipt', icon: Receipt, category: 'Payment' },
    { name: 'QrCode', icon: QrCode, category: 'Payment' },
    { name: 'ScanLine', icon: ScanLine, category: 'Payment' },
    { name: 'Send', icon: Send, category: 'Payment' },
    { name: 'ArrowUpRight', icon: ArrowUpRight, category: 'Payment' },
    { name: 'ArrowDownLeft', icon: ArrowDownLeft, category: 'Payment' },
    { name: 'Repeat', icon: Repeat, category: 'Payment' },
    { name: 'Clock', icon: Clock, category: 'Time' },
    { name: 'Calendar', icon: Calendar, category: 'Time' },
    { name: 'Check', icon: Check, category: 'Status' },
    { name: 'XIcon', icon: XIcon, category: 'Status' },
    { name: 'AlertTriangle', icon: AlertTriangle, category: 'Status' },
    { name: 'Info', icon: Info, category: 'Status' },
    { name: 'HelpCircle', icon: HelpCircle, category: 'Status' },
    { name: 'Heart', icon: Heart, category: 'Support' },
    { name: 'Users', icon: Users, category: 'Support' },
    { name: 'Sun', icon: Sun, category: 'Nature' },
    { name: 'Moon', icon: Moon, category: 'Nature' },
    { name: 'Star', icon: Star, category: 'Support' },
    { name: 'Cloud', icon: Cloud, category: 'Nature' }
];

// ============================================
// DEFAULT PAYMENT METHODS (Zambia-Specific)
// ============================================

const DEFAULT_PAYMENT_METHODS: PaymentMethod[] = [
    { 
        name: 'Airtel Money', 
        slug: 'airtel-money', 
        type: 'mobile_money', 
        provider: 'Airtel', 
        icon: 'Smartphone', 
        isActive: true, 
        sortOrder: 1, 
        isInternational: false,
        instructions: 'Dial *111# and follow the prompts to send money' 
    },
    { 
        name: 'MTN Mobile Money', 
        slug: 'mtn-money', 
        type: 'mobile_money', 
        provider: 'MTN', 
        icon: 'Smartphone', 
        isActive: true, 
        sortOrder: 2, 
        isInternational: false,
        instructions: 'Dial *155# and follow the prompts to send money' 
    },
    { 
        name: 'Zamtel Money', 
        slug: 'zamtel-money', 
        type: 'mobile_money', 
        provider: 'Zamtel', 
        icon: 'Smartphone', 
        isActive: true, 
        sortOrder: 3, 
        isInternational: false 
    },
    { 
        name: 'ZedMobile', 
        slug: 'zedmobile', 
        type: 'mobile_money', 
        provider: 'ZedMobile', 
        icon: 'Smartphone', 
        isActive: true, 
        sortOrder: 4, 
        isInternational: false 
    },
    { 
        name: 'Bank Transfer', 
        slug: 'bank-transfer', 
        type: 'bank_transfer', 
        icon: 'Building2', 
        isActive: true, 
        sortOrder: 5, 
        isInternational: true,
        instructions: 'Use the bank details provided at checkout' 
    },
    { 
        name: 'Card Payment', 
        slug: 'card-payment', 
        type: 'card', 
        icon: 'CreditCard', 
        isActive: true, 
        sortOrder: 6, 
        isInternational: true,
        instructions: 'Visa, Mastercard, and other major cards accepted' 
    }
];

// ============================================
// COMPONENT
// ============================================

export const PaymentMethodManager: React.FC<PaymentMethodManagerProps> = ({ onSave }) => {
    const [methods, setMethods] = useState<PaymentMethod[]>(DEFAULT_PAYMENT_METHODS);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [isAdding, setIsAdding] = useState(false);
    const [formData, setFormData] = useState<Partial<PaymentMethod>>({});

    // ============================================
    // HANDLERS
    // ============================================

    const handleAdd = () => {
        setIsAdding(true);
        setFormData({ 
            name: '', 
            slug: '', 
            type: 'mobile_money', 
            icon: 'Smartphone', 
            isActive: true,
            isInternational: false
        });
        setError(null);
    };

    const handleEdit = (method: PaymentMethod) => {
        setEditingId(method._id || method.name);
        setFormData(method);
        setError(null);
    };

    const handleCancel = () => {
        setEditingId(null);
        setIsAdding(false);
        setFormData({});
        setError(null);
    };

    const validateForm = (): boolean => {
        if (!formData.name?.trim()) {
            setError('Payment method name is required');
            return false;
        }
        if (!formData.slug?.trim()) {
            setError('Slug is required');
            return false;
        }
        return true;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            if (isAdding) {
                const newMethod = {
                    ...formData,
                    _id: `temp-${Date.now()}`,
                    sortOrder: methods.length + 1
                } as PaymentMethod;
                setMethods([...methods, newMethod]);
                setSuccess('Payment method added successfully!');
            } else if (editingId) {
                setMethods(methods.map(m => 
                    (m._id === editingId || m.name === editingId) 
                        ? { ...m, ...formData } as PaymentMethod 
                        : m
                ));
                setSuccess('Payment method updated successfully!');
            }

            if (onSave) {
                onSave(methods);
            }

            handleCancel();
        } catch (err) {
            setError('Failed to save payment method. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this payment method?')) {
            return;
        }

        setLoading(true);
        try {
            setMethods(methods.filter(m => m._id !== id && m.name !== id));
            setSuccess('Payment method deleted successfully!');
        } catch (err) {
            setError('Failed to delete payment method. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const toggleActive = (id: string) => {
        setMethods(methods.map(m => 
            (m._id === id || m.name === id) 
                ? { ...m, isActive: !m.isActive } 
                : m
        ));
    };

    // ============================================
    // RENDER HELPERS
    // ============================================

    const renderIcon = (iconName: string, size: number = 20) => {
        const IconComponent = ICON_MAP[iconName];
        if (!IconComponent) {
            return <Smartphone size={size} className="text-gray-400" />;
        }
        return <IconComponent size={size} className="text-gray-600" />;
    };

    const typeLabels: Record<string, string> = {
        mobile_money: 'Mobile Money',
        bank_transfer: 'Bank Transfer',
        card: 'Card Payment',
        other: 'Other'
    };

    // ============================================
    // RENDER
    // ============================================

    if (loading && methods.length === 0) {
        return (
            <div className="loading-state">
                <Loader2 size={32} className="spinner" />
                <p>Loading payment methods...</p>
            </div>
        );
    }

    return (
        <div className="payment-method-manager">
            <div className="manager-header">
                <div className="header-left">
                    <h3>Payment Methods</h3>
                    <span className="count-badge">{methods.length} methods</span>
                </div>
                <button className="btn-primary" onClick={handleAdd} disabled={loading || isAdding}>
                    <Plus size={16} />
                    Add Payment Method
                </button>
            </div>

            {error && (
                <div className="error-message">
                    <AlertCircle size={16} />
                    {error}
                    <button className="close-error" onClick={() => setError(null)}>✕</button>
                </div>
            )}

            {success && (
                <div className="success-message">
                    <CheckCircle size={16} />
                    {success}
                    <button className="close-success" onClick={() => setSuccess(null)}>✕</button>
                </div>
            )}

            <div className="payment-method-list">
                {methods.map((method, index) => (
                    <div key={method._id || method.name} className={`payment-method-item ${!method.isActive ? 'inactive' : ''}`}>
                        {editingId === (method._id || method.name) ? (
                            <div className="method-edit-form">
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Method Name</label>
                                        <input
                                            type="text"
                                            value={formData.name || ''}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            placeholder="e.g., Airtel Money"
                                            disabled={loading}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Slug</label>
                                        <input
                                            type="text"
                                            value={formData.slug || ''}
                                            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                                            placeholder="e.g., airtel-money"
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Type</label>
                                        <select
                                            value={formData.type || 'mobile_money'}
                                            onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                                            disabled={loading}
                                        >
                                            <option value="mobile_money">Mobile Money</option>
                                            <option value="bank_transfer">Bank Transfer</option>
                                            <option value="card">Card Payment</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Provider (Optional)</label>
                                        <input
                                            type="text"
                                            value={formData.provider || ''}
                                            onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                                            placeholder="e.g., Airtel"
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Icon</label>
                                        <select
                                            value={formData.icon || 'Smartphone'}
                                            onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                                            disabled={loading}
                                        >
                                            <optgroup label="Mobile">
                                                {AVAILABLE_ICONS.filter(i => i.category === 'Mobile').map((icon) => (
                                                    <option key={icon.name} value={icon.name}>
                                                        {icon.name}
                                                    </option>
                                                ))}
                                            </optgroup>
                                            <optgroup label="Banking">
                                                {AVAILABLE_ICONS.filter(i => i.category === 'Banking').map((icon) => (
                                                    <option key={icon.name} value={icon.name}>
                                                        {icon.name}
                                                    </option>
                                                ))}
                                            </optgroup>
                                            <optgroup label="Payment">
                                                {AVAILABLE_ICONS.filter(i => i.category === 'Payment').map((icon) => (
                                                    <option key={icon.name} value={icon.name}>
                                                        {icon.name}
                                                    </option>
                                                ))}
                                            </optgroup>
                                            <optgroup label="Currency">
                                                {AVAILABLE_ICONS.filter(i => i.category === 'Currency').map((icon) => (
                                                    <option key={icon.name} value={icon.name}>
                                                        {icon.name}
                                                    </option>
                                                ))}
                                            </optgroup>
                                            <optgroup label="Security">
                                                {AVAILABLE_ICONS.filter(i => i.category === 'Security').map((icon) => (
                                                    <option key={icon.name} value={icon.name}>
                                                        {icon.name}
                                                    </option>
                                                ))}
                                            </optgroup>
                                            <optgroup label="Other">
                                                {AVAILABLE_ICONS.filter(i => i.category === 'Status' || i.category === 'Time' || i.category === 'Support' || i.category === 'Nature').map((icon) => (
                                                    <option key={icon.name} value={icon.name}>
                                                        {icon.name}
                                                    </option>
                                                ))}
                                            </optgroup>
                                        </select>
                                    </div>
                                    <div className="form-group checkbox-group">
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={formData.isInternational !== false}
                                                onChange={(e) => setFormData({ ...formData, isInternational: e.target.checked })}
                                                disabled={loading}
                                            />
                                            International
                                        </label>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={formData.isActive !== false}
                                                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                                disabled={loading}
                                            />
                                            Active
                                        </label>
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group full-width">
                                        <label>Instructions (Optional)</label>
                                        <textarea
                                            value={formData.instructions || ''}
                                            onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                                            placeholder="Payment instructions for donors..."
                                            rows={2}
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                                <div className="edit-actions">
                                    <button className="btn-primary" onClick={handleSave} disabled={loading}>
                                        {loading ? <Loader2 size={14} className="spinner" /> : <Save size={14} />}
                                        {loading ? 'Saving...' : 'Save'}
                                    </button>
                                    <button className="btn-secondary" onClick={handleCancel} disabled={loading}>
                                        <X size={14} />
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="method-info">
                                    <div className="method-icon-wrapper">
                                        {renderIcon(method.icon || 'Smartphone', 20)}
                                    </div>
                                    <div className="method-details">
                                        <span className="method-name">
                                            {method.name}
                                            {method.isInternational && (
                                                <span className="international-badge">🌍 International</span>
                                            )}
                                        </span>
                                        <span className="method-type">{typeLabels[method.type]}</span>
                                        {method.provider && (
                                            <span className="method-provider">Provider: {method.provider}</span>
                                        )}
                                        {method.instructions && (
                                            <span className="method-instructions">{method.instructions}</span>
                                        )}
                                    </div>
                                    <span className={`method-status ${method.isActive ? 'active' : 'inactive'}`}>
                                        {method.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                                <div className="method-actions">
                                    <button 
                                        className="action-btn" 
                                        onClick={() => toggleActive(method._id || method.name)}
                                        title={method.isActive ? 'Deactivate' : 'Activate'}
                                        disabled={loading}
                                    >
                                        {method.isActive ? <Eye size={14} /> : <EyeOff size={14} />}
                                    </button>
                                    <button className="action-btn" onClick={() => handleEdit(method)} title="Edit" disabled={loading}>
                                        <Edit size={14} />
                                    </button>
                                    <button 
                                        className="action-btn danger" 
                                        onClick={() => handleDelete(method._id || method.name)}
                                        title="Delete"
                                        disabled={loading}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </div>

            {isAdding && (
                <div className="payment-method-list">
                    <div className="payment-method-item adding">
                        <div className="method-edit-form">
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Method Name</label>
                                    <input
                                        type="text"
                                        value={formData.name || ''}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="e.g., Airtel Money"
                                        disabled={loading}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Slug</label>
                                    <input
                                        type="text"
                                        value={formData.slug || ''}
                                        onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                                        placeholder="e.g., airtel-money"
                                        disabled={loading}
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Type</label>
                                    <select
                                        value={formData.type || 'mobile_money'}
                                        onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                                        disabled={loading}
                                    >
                                        <option value="mobile_money">Mobile Money</option>
                                        <option value="bank_transfer">Bank Transfer</option>
                                        <option value="card">Card Payment</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Provider (Optional)</label>
                                    <input
                                        type="text"
                                        value={formData.provider || ''}
                                        onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                                        placeholder="e.g., Airtel"
                                        disabled={loading}
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Icon</label>
                                    <select
                                        value={formData.icon || 'Smartphone'}
                                        onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                                        disabled={loading}
                                    >
                                        <optgroup label="Mobile">
                                            {AVAILABLE_ICONS.filter(i => i.category === 'Mobile').map((icon) => (
                                                <option key={icon.name} value={icon.name}>
                                                    {icon.name}
                                                </option>
                                            ))}
                                        </optgroup>
                                        <optgroup label="Banking">
                                            {AVAILABLE_ICONS.filter(i => i.category === 'Banking').map((icon) => (
                                                <option key={icon.name} value={icon.name}>
                                                    {icon.name}
                                                </option>
                                            ))}
                                        </optgroup>
                                        <optgroup label="Payment">
                                            {AVAILABLE_ICONS.filter(i => i.category === 'Payment').map((icon) => (
                                                <option key={icon.name} value={icon.name}>
                                                    {icon.name}
                                                </option>
                                            ))}
                                        </optgroup>
                                        <optgroup label="Currency">
                                            {AVAILABLE_ICONS.filter(i => i.category === 'Currency').map((icon) => (
                                                <option key={icon.name} value={icon.name}>
                                                    {icon.name}
                                                </option>
                                            ))}
                                        </optgroup>
                                        <optgroup label="Security">
                                            {AVAILABLE_ICONS.filter(i => i.category === 'Security').map((icon) => (
                                                <option key={icon.name} value={icon.name}>
                                                    {icon.name}
                                                </option>
                                            ))}
                                        </optgroup>
                                        <optgroup label="Other">
                                            {AVAILABLE_ICONS.filter(i => i.category === 'Status' || i.category === 'Time' || i.category === 'Support' || i.category === 'Nature').map((icon) => (
                                                <option key={icon.name} value={icon.name}>
                                                    {icon.name}
                                                </option>
                                            ))}
                                        </optgroup>
                                    </select>
                                </div>
                                <div className="form-group checkbox-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={formData.isInternational !== false}
                                            onChange={(e) => setFormData({ ...formData, isInternational: e.target.checked })}
                                            disabled={loading}
                                        />
                                        International
                                    </label>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={formData.isActive !== false}
                                            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                            disabled={loading}
                                        />
                                        Active
                                    </label>
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group full-width">
                                    <label>Instructions (Optional)</label>
                                    <textarea
                                        value={formData.instructions || ''}
                                        onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                                        placeholder="Payment instructions for donors..."
                                        rows={2}
                                        disabled={loading}
                                    />
                                </div>
                            </div>
                            <div className="edit-actions">
                                <button className="btn-primary" onClick={handleSave} disabled={loading}>
                                    {loading ? <Loader2 size={14} className="spinner" /> : <Save size={14} />}
                                    {loading ? 'Saving...' : 'Add Method'}
                                </button>
                                <button className="btn-secondary" onClick={handleCancel} disabled={loading}>
                                    <X size={14} />
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentMethodManager;
